/**
 * 03b / 03c · Scratch the card.
 *
 * A real scratch layer: a canvas over the coupon, erased with
 * `globalCompositeOperation = 'destination-out'` along the pointer's path. At
 * ~45% cleared the layer lifts and the CTA unlocks — the same threshold the
 * handoff specifies, low enough that nobody has to erase the whole card.
 *
 * The discount is verified against Polar before this screen renders at all. If
 * the configured code is not a live discount the step reports itself skippable
 * and the funnel moves on, because a percentage we cannot charge is not an
 * offer, it is a lie the checkout would expose.
 *
 * Both states are the same height, so revealing does not make the page jump.
 */
import React from 'react';
import { Icon } from '../app/icons';
import { c, f, ACT } from '../app/tokens';
import { Col, Meta, Muted, Row, Appear, usePrefersReducedMotion } from '../app/primitives';
import { useTt } from '../i18n/useT';
import { PROMO_MINUTES, PromoState } from '../config/paywall';
import { Cta, OK, Screen, Serif, StickyActions, TopBar, useWide } from './ui';

/**
 * Fraction of the foil that must be gone before the coupon opens.
 *
 * Deliberately well under half: past a couple of strokes the person has already
 * shown intent, and making them colour in the whole card turns a small reward
 * into a chore.
 */
const THRESHOLD = 0.26;

/** Stroke width of the finger's trail, in CSS pixels. */
const LINE = 62;

/** Paint the foil: base sheet, two rulings, one sheen. */
function paintFoil(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    const base = ctx.createLinearGradient(0, 0, w, h);

    base.addColorStop(0, '#4a453e');
    base.addColorStop(0.26, '#5b544a');
    base.addColorStop(0.52, '#38332d');
    base.addColorStop(0.74, '#524b42');
    base.addColorStop(1, '#3d382f');
    ctx.fillStyle = base;
    ctx.fillRect(0, 0, w, h);

    const rule = (angle: number, step: number, color: string, width: number) => {
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.translate(w / 2, h / 2);
        ctx.rotate((angle * Math.PI) / 180);
        const span = Math.hypot(w, h);

        for (let x = -span; x < span; x += step) {
            ctx.beginPath();
            ctx.moveTo(x, -span);
            ctx.lineTo(x, span);
            ctx.stroke();
        }
        ctx.restore();
    };

    rule(22, 4, 'rgba(255,255,255,0.055)', 1);
    rule(-26, 9, 'rgba(0,0,0,0.14)', 1);

    const sheen = ctx.createLinearGradient(0, h, w, 0);

    sheen.addColorStop(0.26, 'rgba(255,255,255,0)');
    sheen.addColorStop(0.4, 'rgba(255,255,255,0.09)');
    sheen.addColorStop(0.56, 'rgba(255,255,255,0)');
    ctx.fillStyle = sheen;
    ctx.fillRect(0, 0, w, h);
}

/**
 * The scratch layer.
 *
 * Sizing comes from a ResizeObserver rather than a fixed number: the coupon's
 * height depends on the rendered copy, and a canvas that is one line short
 * leaves a strip of coupon showing before anyone touches it.
 */
const Foil: React.FC<{ onCleared: () => void }> = ({ onCleared }) => {
    const wrap = React.useRef<HTMLDivElement>(null);
    const canvas = React.useRef<HTMLCanvasElement>(null);
    const drawing = React.useRef(false);
    const last = React.useRef<{ x: number; y: number } | null>(null);
    const moves = React.useRef(0);
    const done = React.useRef(false);

    // Redraw the foil whenever the box resizes; a canvas keeps its bitmap but
    // not its scale, so a rotation would otherwise smear it.
    React.useEffect(() => {
        const box = wrap.current;
        const cnv = canvas.current;

        if (!box || !cnv) return undefined;

        const fit = () => {
            const { width, height } = box.getBoundingClientRect();

            if (!width || !height) return;
            const dpr = window.devicePixelRatio || 1;

            cnv.width = Math.round(width * dpr);
            cnv.height = Math.round(height * dpr);
            cnv.style.width = `${width}px`;
            cnv.style.height = `${height}px`;
            const ctx = cnv.getContext('2d');

            if (!ctx) return;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.globalCompositeOperation = 'source-over';
            paintFoil(ctx, width, height);
            ctx.lineWidth = LINE;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        };

        fit();
        const ro = new ResizeObserver(fit);

        ro.observe(box);
        return () => ro.disconnect();
    }, []);

    /** Cleared fraction, sampled on a small offscreen copy rather than full-res. */
    const cleared = React.useCallback((): number => {
        const cnv = canvas.current;

        if (!cnv) return 0;
        const ctx = cnv.getContext('2d');

        if (!ctx) return 0;
        // A 60-wide read is ~2000 pixels instead of a million and answers the
        // only question we have: roughly how much is gone.
        const w = 60;
        const h = Math.max(1, Math.round((cnv.height / cnv.width) * w));

        try {
            const { data } = ctx.getImageData(0, 0, cnv.width, cnv.height);
            const stepX = Math.max(1, Math.floor(cnv.width / w));
            const stepY = Math.max(1, Math.floor(cnv.height / h));
            let seen = 0;
            let clear = 0;

            for (let y = 0; y < cnv.height; y += stepY) {
                for (let x = 0; x < cnv.width; x += stepX) {
                    seen += 1;
                    if (data[(y * cnv.width + x) * 4 + 3] < 24) clear += 1;
                }
            }

            return seen ? clear / seen : 0;
        } catch {
            return 0;
        }
    }, []);

    const at = (e: React.PointerEvent<HTMLCanvasElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();

        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const erase = (to: { x: number; y: number }) => {
        const ctx = canvas.current?.getContext('2d');

        if (!ctx) return;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.beginPath();
        const from = last.current || to;

        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
        last.current = to;
    };

    const onDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (done.current) return;
        drawing.current = true;
        last.current = null;
        e.currentTarget.setPointerCapture(e.pointerId);
        erase(at(e));
    };

    const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (!drawing.current || done.current) return;
        erase(at(e));
        moves.current += 1;
        // Reading pixels is the expensive part, so it happens every eighth move
        // rather than on each one.
        if (moves.current % 8 !== 0) return;
        if (cleared() >= THRESHOLD) {
            done.current = true;
            drawing.current = false;
            onCleared();
        }
    };

    const onUp = () => {
        drawing.current = false;
        last.current = null;
        if (done.current) return;
        if (cleared() >= THRESHOLD) {
            done.current = true;
            onCleared();
        }
    };

    return (
        <div ref={wrap} style={{ position: 'absolute', inset: 0, borderRadius: 20, overflow: 'hidden' }}>
            <canvas
                ref={canvas}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={onUp}
                onPointerCancel={onUp}
                style={{ display: 'block', touchAction: 'none', cursor: 'grab' }}
            />
        </div>
    );
};

const Coupon: React.FC<{ percent: number; wide?: boolean; revealed: boolean }> = ({ percent, wide, revealed }) => {
    const { t } = useTt();

    return (
        <Col
            style={{
                background: c.card,
                border: `1px solid ${ACT}${revealed ? '55' : '2e'}`,
                borderRadius: wide ? 24 : 20,
                padding: wide ? '40px 28px 34px' : '30px 22px 26px',
                alignItems: 'center',
                transition: 'border-color 400ms',
            }}
            gap={0}
        >
            <Meta style={{ fontSize: wide ? 10 : 9.5, color: c.muted, letterSpacing: '0.16em' }}>
                {t('pw.sc.coupon.label')}
            </Meta>
            <span
                style={{
                    fontFamily: f.mono,
                    fontSize: wide ? 96 : 78,
                    fontWeight: 200,
                    color: ACT,
                    letterSpacing: '-0.055em',
                    lineHeight: 0.95,
                    whiteSpace: 'nowrap',
                    marginTop: wide ? 16 : 14,
                }}
            >
                {percent}%
            </span>
            <div
                style={{
                    fontSize: wide ? 19 : 17,
                    color: c.textPri,
                    fontWeight: 400,
                    letterSpacing: '-0.018em',
                    marginTop: wide ? 14 : 12,
                }}
            >
                {t('pw.sc.coupon.off')}
            </div>
            <Muted style={{ fontSize: wide ? 13 : 12.5, marginTop: 8, textAlign: 'center' }}>
                {t('pw.sc.coupon.note')}
            </Muted>
        </Col>
    );
};

const Perks: React.FC<{ revealed: boolean; wide?: boolean }> = ({ revealed, wide }) => {
    const { t } = useTt();

    return (
        <Col gap={wide ? 11 : 9}>
            {['pw.sc.perk1', 'pw.sc.perk2', 'pw.sc.perk3'].map((k) => (
                <Row key={k} style={{ gap: wide ? 12 : 11 }}>
                    <Icon
                        k={revealed ? 'check' : 'lock'}
                        size={wide ? 13 : 12}
                        color={revealed ? OK : 'rgba(232,230,224,0.3)'}
                    />
                    <span
                        style={{
                            fontSize: wide ? 13.5 : 12.5,
                            color: revealed ? c.text : c.dim,
                            fontWeight: 300,
                            transition: 'color 400ms',
                        }}
                    >
                        {t(k)}
                    </span>
                </Row>
            ))}
        </Col>
    );
};

interface Props {
    promo: PromoState;
    /** Called with the accepted promo once the person claims it. */
    onClaim: (promo: PromoState) => void;
    /** Fired on reveal, before the claim — this is when the countdown starts. */
    onReveal?: () => void;
    onBack?: () => void;
}

const Scratch: React.FC<Props> = ({ promo, onClaim, onReveal, onBack }) => {
    const { t } = useTt();
    const wide = useWide();
    const reduced = usePrefersReducedMotion();
    // Someone who cannot make a swipe gesture must still be able to take the
    // discount, so reduced-motion opens the card outright.
    const [revealed, setRevealed] = React.useState(reduced);
    const announced = React.useRef(false);

    const reveal = React.useCallback(() => {
        setRevealed(true);
        if (announced.current) return;
        announced.current = true;
        onReveal?.();
    }, [onReveal]);

    React.useEffect(() => {
        if (reduced) reveal();
    }, [reduced, reveal]);

    const head = (
        <>
            <Meta style={{ fontSize: wide ? 10 : 9.5, color: ACT, letterSpacing: wide ? '0.18em' : '0.16em' }}>
                {t(revealed ? 'pw.sc.eyebrow.unlocked' : 'pw.sc.eyebrow.locked')}
            </Meta>
            <Serif size={wide ? 38 : 25} style={{ marginTop: 13, lineHeight: 1.2 }}>
                {t(revealed ? 'pw.sc.title.unlocked' : 'pw.sc.title.locked')}
            </Serif>
            <Muted
                style={{ fontSize: wide ? 15 : 13, marginTop: 10, lineHeight: 1.5, maxWidth: wide ? 460 : undefined }}
            >
                {t(revealed ? 'pw.sc.sub.unlocked' : 'pw.sc.sub.locked')}
            </Muted>
        </>
    );

    const action = revealed ? (
        <Cta
            label={t('pw.sc.cta.unlocked')}
            note={t('pw.sc.foot.unlocked', { n: PROMO_MINUTES })}
            onClick={() => onClaim(promo)}
            width={wide ? 340 : '100%'}
            height={wide ? 54 : 50}
        />
    ) : (
        <Col style={{ alignItems: wide ? 'flex-start' : 'center', width: '100%' }} gap={12}>
            <Row
                style={{
                    width: wide ? 340 : '100%',
                    height: wide ? 54 : 50,
                    borderRadius: 99,
                    background: 'rgba(232,230,224,0.06)',
                    justifyContent: 'center',
                }}
            >
                <span style={{ fontSize: 14.5, fontWeight: 500, color: 'rgba(232,230,224,0.32)' }}>
                    {t('pw.sc.cta.locked')}
                </span>
            </Row>
            <Row style={{ width: wide ? 340 : '100%', justifyContent: 'center', gap: 9 }}>
                <Icon k="arrow" size={13} color={c.muted} />
                <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.16em' }}>{t('pw.sc.hint')}</Meta>
            </Row>
        </Col>
    );

    const coupon = (
        <div style={{ position: 'relative' }}>
            <Coupon percent={promo.percent} wide={wide} revealed={revealed} />
            {!revealed && <Foil onCleared={reveal} />}
        </div>
    );

    if (wide) {
        return (
            <Screen wide style={{ padding: '38px 40px 56px' }}>
                <Appear>
                    <Row style={{ gap: 40, maxWidth: 1020, margin: '26px auto 0', alignItems: 'center' }}>
                        <Col style={{ flex: 1, minWidth: 0 }} gap={16}>
                            {head}
                            <div style={{ marginTop: 6 }}>
                                <Perks revealed={revealed} wide />
                            </div>
                            <div style={{ marginTop: 10 }}>{action}</div>
                        </Col>
                        <Col style={{ width: 440, flex: '0 0 auto' }}>{coupon}</Col>
                    </Row>
                </Appear>
            </Screen>
        );
    }

    return (
        <Screen column>
            <TopBar onBack={onBack} />
            <Appear style={{ flex: 1, minHeight: 0 }}>
                <div style={{ padding: '34px 26px 0', textAlign: 'center' }}>{head}</div>
                <div style={{ margin: '30px 26px 0' }}>{coupon}</div>
                <div style={{ padding: '30px 26px 0' }}>
                    <Perks revealed={revealed} />
                </div>
            </Appear>
            <StickyActions>
                {action}
                {!revealed && (
                    <Row style={{ justifyContent: 'center', marginTop: 13 }}>
                        <Meta style={{ fontSize: 9.5, color: c.muted, letterSpacing: '0.1em', textAlign: 'center' }}>
                            {t('pw.sc.foot.locked')}
                        </Meta>
                    </Row>
                )}
            </StickyActions>
        </Screen>
    );
};

export default Scratch;
